import { useState } from "react";
import { ShieldAlert, CheckCircle2, XCircle, HelpCircle, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCreateTrustedSender, getListTrustedSendersQueryKey, useListTrustedSenders } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { normalizeTrustedDomain } from "@/lib/trustedSenders";
import { apiErrorMessage, apiErrorStatus } from "@/lib/apiError";
import type { VerificationStatus } from "./VerifiedSenderBadge";

export interface VerificationDetails {
  spf: string;
  dkim: string;
  dmarc: string;
}

interface FailedVerificationWarningProps {
  verificationStatus: VerificationStatus;
  verificationDetails?: VerificationDetails | null;
  senderDomain?: string | null;
  isAdmin?: boolean;
  className?: string;
  testIdSuffix?: string;
}

function CheckIcon({ result }: { result: string }) {
  if (result === "pass") {
    return <CheckCircle2 className="h-3 w-3 shrink-0 text-green-600 dark:text-green-400" />;
  }
  if (result === "unknown") {
    return <HelpCircle className="h-3 w-3 shrink-0 text-muted-foreground" />;
  }
  return <XCircle className="h-3 w-3 shrink-0 text-red-600 dark:text-red-400" />;
}

function resultLabel(result: string): string {
  const labels: Record<string, string> = {
    pass: "Pass",
    fail: "Fail",
    softfail: "Soft fail",
    neutral: "Neutral",
    none: "None",
    permerror: "Error",
    temperror: "Temp error",
    unknown: "Unknown",
  };
  return labels[result] ?? result;
}

function extractDomain(email: string): string | null {
  const at = email.lastIndexOf("@");
  if (at < 0) return null;
  return normalizeTrustedDomain(email.slice(at + 1));
}

function domainMatchesTrusted(
  senderDomain: string,
  trustedDomain: string,
): boolean {
  return (
    senderDomain === trustedDomain ||
    senderDomain.endsWith(`.${trustedDomain}`)
  );
}

function TrustSenderAction({
  senderDomain,
  isAdmin,
  testIdSuffix,
}: {
  senderDomain: string;
  isAdmin: boolean;
  testIdSuffix: string;
}) {
  const queryClient = useQueryClient();
  const createMutation = useCreateTrustedSender();
  const listQuery = useListTrustedSenders();
  const [optimisticTrusted, setOptimisticTrusted] = useState(false);

  const domain = extractDomain(senderDomain) ?? normalizeTrustedDomain(senderDomain);

  if (!domain) return null;

  const alreadyTrusted = optimisticTrusted || (() => {
    const items = listQuery.data?.items;
    if (!items || items.length === 0) return false;
    return items.some((t) => domainMatchesTrusted(domain, t.domain));
  })();

  if (alreadyTrusted) {
    return (
      <div
        className="flex items-center gap-1.5 border-t pt-2 mt-2 border-border/50 text-green-600 dark:text-green-400"
        data-testid={`trust-sender-done${testIdSuffix}`}
      >
        <ShieldCheck className="h-3 w-3 shrink-0" />
        <span className="text-[11px]">{domain} trusted</span>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <p
        className="border-t pt-2 mt-2 border-border/50 text-muted-foreground italic"
        data-testid={`trust-sender-admin-hint${testIdSuffix}`}
      >
        Ask your workspace admin to trust this sender.
      </p>
    );
  }

  const handleTrust = async () => {
    try {
      await createMutation.mutateAsync({ data: { domain } });
      setOptimisticTrusted(true);
      queryClient.invalidateQueries({ queryKey: getListTrustedSendersQueryKey() });
      toast.success(`${domain} added to trusted senders`);
    } catch (e) {
      if (apiErrorStatus(e) === 409) {
        setOptimisticTrusted(true);
        queryClient.invalidateQueries({ queryKey: getListTrustedSendersQueryKey() });
        return;
      }
      toast.error(apiErrorMessage(e, "Failed to trust sender"));
    }
  };

  return (
    <div className="border-t pt-2 mt-2 border-border/50">
      <Button
        variant="outline"
        size="sm"
        className="h-6 text-[11px] w-full"
        onClick={(e) => {
          e.stopPropagation();
          void handleTrust();
        }}
        disabled={createMutation.isPending}
        data-testid={`button-trust-sender${testIdSuffix}`}
      >
        {createMutation.isPending ? (
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
        ) : (
          <ShieldCheck className="h-3 w-3 mr-1" />
        )}
        Trust this sender
      </Button>
    </div>
  );
}

export function FailedVerificationWarning({
  verificationStatus,
  verificationDetails,
  senderDomain,
  isAdmin,
  className,
  testIdSuffix = "",
}: FailedVerificationWarningProps) {
  if (verificationStatus !== "fail") {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 shrink-0 cursor-help rounded-sm px-1.5 py-0.5 text-[11px] font-medium leading-tight",
            "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          )}
          data-testid={`failed-verification-warning${testIdSuffix}`}
          data-verification-status="fail"
          aria-label="Failed authentication checks"
          role="status"
          tabIndex={0}
        >
          <ShieldAlert className={cn("h-3 w-3 shrink-0", className)} />
          <span className="hidden sm:inline">Failed auth</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-snug" onClick={(e) => e.stopPropagation()}>
        <p className="font-medium mb-1">Failed authentication checks</p>
        <p className="text-muted-foreground mb-2">
          This message did not pass DMARC alignment — the sending domain
          could not be verified. Treat the claimed sender with caution.
        </p>
        {verificationDetails && (
          <div className="space-y-1 border-t pt-2 border-border/50">
            <div className="flex items-center gap-1.5">
              <CheckIcon result={verificationDetails.spf} />
              <span className="font-medium w-11">SPF</span>
              <span className="text-muted-foreground">{resultLabel(verificationDetails.spf)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckIcon result={verificationDetails.dkim} />
              <span className="font-medium w-11">DKIM</span>
              <span className="text-muted-foreground">{resultLabel(verificationDetails.dkim)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckIcon result={verificationDetails.dmarc} />
              <span className="font-medium w-11">DMARC</span>
              <span className="text-muted-foreground">{resultLabel(verificationDetails.dmarc)}</span>
            </div>
          </div>
        )}
        {senderDomain && isAdmin !== undefined && (
          <TrustSenderAction
            senderDomain={senderDomain}
            isAdmin={isAdmin}
            testIdSuffix={testIdSuffix}
          />
        )}
      </TooltipContent>
    </Tooltip>
  );
}
